import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'
import { Link } from 'react-router-dom'
import { useCookieConsent, CookieConsent } from '@/contexts/CookieConsentContext'

interface CookieCategoryInfo {
  key: keyof CookieConsent
  title: string
  description: string
  required?: boolean
  cookies: Array<{
    name: string
    purpose: string
    duration: string
    provider?: string
  }>
}

const cookieCategories: CookieCategoryInfo[] = [
  {
    key: 'necessary',
    title: 'Technisch notwendig',
    description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.',
    required: true,
    cookies: [
      {
        name: 'sb-*',
        purpose: 'Authentifizierung und Sitzungsverwaltung',
        duration: 'Sitzung / 7 Tage',
        provider: 'Supabase'
      },
      {
        name: 'sidebar:state',
        purpose: 'Speichert den Zustand der Navigation',
        duration: 'Persistent',
        provider: 'Erste Partei'
      }
    ]
  },
  {
    key: 'functional',
    title: 'Funktional',
    description: 'Diese Cookies ermöglichen erweiterte Funktionen und Personalisierungen.',
    cookies: [
      {
        name: 'theme',
        purpose: 'Speichert die gewählte Farbschema-Einstellung',
        duration: 'Persistent',
        provider: 'Erste Partei'
      },
      {
        name: 'language',
        purpose: 'Speichert die Spracheinstellung',
        duration: 'Persistent',
        provider: 'Erste Partei'
      }
    ]
  },
  {
    key: 'analytics',
    title: 'Analyse',
    description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.',
    cookies: [
      {
        name: '_ga',
        purpose: 'Unterscheidet Benutzer',
        duration: '2 Jahre',
        provider: 'Google Analytics'
      },
      {
        name: '_ga_*',
        purpose: 'Speichert Session-Informationen',
        duration: '2 Jahre',
        provider: 'Google Analytics'
      }
    ]
  },
  {
    key: 'marketing',
    title: 'Marketing',
    description: 'Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen.',
    cookies: [
      {
        name: '_fbp',
        purpose: 'Facebook Pixel Tracking',
        duration: '3 Monate',
        provider: 'Meta'
      },
      {
        name: '_fbc',
        purpose: 'Facebook Conversion Tracking',
        duration: '1 Jahr',
        provider: 'Meta'
      }
    ]
  }
]

export const CookieSettingsModal = () => {
  const { consent, showSettings, setShowSettings, updateConsent } = useCookieConsent()
  const [tempConsent, setTempConsent] = useState<CookieConsent>(consent)

  useEffect(() => {
    setTempConsent(consent)
  }, [consent, showSettings])

  const handleSave = () => {
    updateConsent(tempConsent)
  }

  const handleToggle = (category: keyof CookieConsent, value: boolean) => {
    if (category === 'necessary') return // Cannot disable necessary cookies
    setTempConsent(prev => ({ ...prev, [category]: value }))
  }

  return (
    <Dialog open={showSettings} onOpenChange={setShowSettings}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie-Einstellungen</DialogTitle>
          <DialogDescription>
            Verwalten Sie Ihre Cookie-Präferenzen. Sie können Ihre Zustimmung jederzeit widerrufen oder ändern.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {cookieCategories.map((category, index) => (
            <div key={category.key}>
              <div className="flex items-center justify-between mb-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold">
                      {category.title}
                    </Label>
                    {category.required && (
                      <Badge variant="secondary" className="text-xs">
                        Erforderlich
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {category.description}
                  </p>
                </div>
                <Switch
                  checked={tempConsent[category.key]}
                  onCheckedChange={(value) => handleToggle(category.key, value)}
                  disabled={category.required}
                />
              </div>

              <div className="space-y-2 ml-4">
                {category.cookies.map((cookie, cookieIndex) => (
                  <div key={cookieIndex} className="text-sm space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-foreground">{cookie.name}</span>
                      <span className="text-xs text-muted-foreground">{cookie.duration}</span>
                    </div>
                    <p className="text-muted-foreground text-xs">{cookie.purpose}</p>
                    {cookie.provider && (
                      <p className="text-muted-foreground text-xs">
                        Anbieter: {cookie.provider}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {index < cookieCategories.length - 1 && <Separator className="mt-4" />}
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button onClick={handleSave} className="flex-1">
            Einstellungen speichern
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowSettings(false)}
            className="flex-1"
          >
            Abbrechen
          </Button>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            Sie können Ihre Cookie-Einstellungen jederzeit in den Einstellungen ändern.
          </p>
          <p>
            Weitere Informationen finden Sie in unserer{' '}
            <Link 
              to="/datenschutz" 
              className="underline hover:text-foreground"
              onClick={() => setShowSettings(false)}
            >
              Datenschutzerklärung
            </Link>{' '}
            und unserem{' '}
            <Link 
              to="/impressum" 
              className="underline hover:text-foreground"
              onClick={() => setShowSettings(false)}
            >
              Impressum
            </Link>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}